import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, ErreurApi } from './api';
import type { DemandeEnAttente } from './useModificationsSensibles';

export type DemiJournee = 'MATIN' | 'APRES_MIDI';
export type TypeSeance = 'PRESENTIEL' | 'ELEARNING';
export type StatutSeance = 'PREVUE' | 'EN_COURS' | 'CLOTUREE';
export type StatutPresence = 'ATTENDU' | 'PRESENT' | 'RETARD' | 'ABSENT';
/**
 * Ce qui s'est réellement passé sur le créneau, **déduit par l'API** des heures
 * réelles (`domain/presence-horaires.ts`). L'écran ne le recalcule pas.
 */
export type QualificationPresence =
  | 'ATTENDU'
  | 'PRESENT'
  | 'RETARD'
  | 'DEPART_ANTICIPE'
  | 'PRESENCE_PARTIELLE'
  | 'ABSENT'
  | 'ABSENCE_JUSTIFIEE';
export type SourceCode = 'SESSION' | 'FORMATION' | 'SEANCE';

/** Où en est l'apprenant dans la salle d'une classe à distance, d'après le dernier relevé. */
export type EtatSalle = 'CAMERA' | 'SANS_CAMERA' | 'HORS_SALLE' | 'JAMAIS_CONNECTE';

/**
 * Relevé de la salle d'une classe à distance pour une ligne, **lu par l'API** sur
 * le serveur de diffusion (`domain/presence-distance.ts`) — jamais déclaré par
 * le navigateur.
 */
export interface ReleveAffiche {
  etat: EtatSalle;
  etatLibelle: string;
  etatTon: string;
  /** « Connecté(e) 08:31 → 11:40 · 3 h 09 · caméra 96 % · 1 coupure caméra ». */
  resume: string;
  arrivee: string | null;
  sortie: string | null;
  minutesConnecte: number;
  partCamera: number | null;
  coupuresCamera: number;
  sortiesTemporaires: number;
  cameraCoupeeDepuis: string | null;
  relances: number;
}

export interface PresenceAffichee {
  id: string;
  apprenantId: string;
  apprenant: string;
  statut: StatutPresence;
  statutLibelle: string;
  statutTon: string;
  justifiee: boolean;
  motifJustification: string | null;
  heureArrivee: string | null;
  heureDepart: string | null;
  qualification: QualificationPresence;
  qualificationLibelle: string;
  qualificationTon: string;
  minutesPrevues: number;
  minutesPresence: number | null;
  minutesRetard: number | null;
  minutesDepartAnticipe: number;
  minutesAbsence: number | null;
  signeLe: string | null;
  signatureDisponible: boolean;
  empreinteSignature: string | null;
  entrepriseNotifieeLe: string | null;
  creeLe: string;
  modifieLe: string;
  appelPar: string | null;
  modifiePar: string | null;
  /** Classe à distance : l'apprenant y émarge d'un geste dans la salle, sans tracé. */
  releve: ReleveAffiche | null;
  /** Correction demandée à la direction, pas encore décidée (TF-437). */
  demandeEnAttente: DemandeEnAttente | null;
}

export interface SeanceAffichee {
  id: string;
  libelle: string;
  formation: string | null;
  sessionId: string | null;
  session: string | null;
  siteCode: string;
  siteNom: string;
  date: string;
  demiJournee: DemiJournee;
  demiJourneeLibelle: string;
  heureDebut: string;
  heureFin: string;
  lieu: string | null;
  type: TypeSeance;
  /** Bloc du programme que la feuille dispense ; `null` : hors bloc (TF-461). */
  bloc: { id: string; libelle: string } | null;
  /** Les blocs du programme de sa formation, dans leur ordre. */
  blocs: { id: string; libelle: string }[];
  /** Pourquoi le bloc ne se choisit pas sur cette feuille. */
  refusBloc: string | null;
  formateur: string | null;
  formateurSigneLe: string | null;
  /** Qui a signé pour le formateur : lui-même, ou le membre du pôle qui anime. */
  formateurSignePar: string | null;
  formateurSignatureDisponible: boolean;
  appelValideLe: string | null;
  appelPar: string | null;
  /** Un code garde la sortie du mode signature, d'où qu'il vienne. */
  pinPose: boolean;
  /** `SESSION` / `FORMATION` : code paramétré, l'appel n'en demande pas. */
  codeSource: SourceCode | null;
  codeSourceLibelle: string | null;
  statut: StatutSeance;
  clotureeLe: string | null;
  /** PV en vigueur : celui de la dernière clôture. */
  documentId: string | null;
  /** Toutes les versions du PV, la plus récente d'abord. */
  pvs: { id: string; genereLe: string }[];
  /** Réouverture demandée à la direction, pas encore décidée (TF-437). */
  reouvertureEnAttente: DemandeEnAttente | null;
  /** Clôturée, le lecteur corrige une présence sans la demander : pôle pédagogique, direction (TF-476). */
  correctionDirecte: boolean;
  /** Rappel d'émargement parti (TF-460) : premier envoi et apprenants rappelés. */
  rappel: { envoyeLe: string; destinataires: number } | null;
  presences: PresenceAffichee[];
  compteurs: {
    inscrits: number;
    presents: number;
    retards: number;
    absents: number;
    attendus: number;
    signatures: number;
  };
}

export interface ReponseSeances {
  seances: SeanceAffichee[];
  jour: string;
  compteurs: {
    seancesDuJour: number;
    cloturees: number;
    signaturesRecueillies: number;
    signaturesAttendues: number;
    tauxPresence: number | null;
  };
  prestataire: { nom: string; niveau: string; operationnel: boolean };
}

export function useSeances(site: string, date: string) {
  const parametres = new URLSearchParams({ site });
  if (date) parametres.set('date', date);

  return useQuery({
    queryKey: ['emargement', site, date],
    queryFn: () => appelApi<ReponseSeances>(`/emargement?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['emargement'] });
  void client.invalidateQueries({ queryKey: ['absences'] });
}

export function useCreerSeance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entree: {
      libelle: string;
      site: string;
      date: string;
      demiJournee: DemiJournee;
      heureDebut: string;
      heureFin: string;
      type?: TypeSeance;
      lieu?: string;
      formationCode?: string;
      apprenants?: string[];
      /** Les seuls apprentis de la formation, sans session. */
      sansSession?: boolean;
      /** Bloc du programme de la formation que la séance dispense (TF-461). */
      moduleFormationId?: string;
    }) => appelApi<SeanceAffichee>('/emargement/seances', { methode: 'POST', corps: entree }),
    onSuccess: () => invalider(client),
  });
}

/** Bloc du programme que la feuille dispense ; `null` la range hors bloc (TF-461). */
export function useChoisirBloc() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ seanceId, moduleFormationId }: { seanceId: string; moduleFormationId: string | null }) =>
      appelApi<SeanceAffichee>(`/emargement/seances/${seanceId}/bloc`, { methode: 'PUT', corps: { moduleFormationId } }),
    onSuccess: () => {
      invalider(client);
      void client.invalidateQueries({ queryKey: ['absenteisme'] });
    },
  });
}

export function useRequalifierPresence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      seanceId: string;
      presenceId: string;
      statut: 'PRESENT' | 'RETARD' | 'ABSENT';
      /** Absente : l'heure en place est gardée ; `null` la retire. */
      heureArrivee?: string | null;
      heureDepart?: string | null;
      justifiee?: boolean;
      motifJustification?: string | null;
      /** Pourquoi la ligne est corrigée ; conservé à l'historique. */
      commentaire?: string;
    }) => {
      const { seanceId, presenceId, ...corps } = variables;
      return appelApi<SeanceAffichee>(
        `/emargement/seances/${seanceId}/presences/${presenceId}`,
        { methode: 'PATCH', corps },
      );
    },
    onSuccess: () => invalider(client),
  });
}

export function useSignerPresence() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { seanceId: string; presenceId: string; trace: string }) =>
      appelApi<{ seance: SeanceAffichee; mention: string; niveau: string }>(
        `/emargement/seances/${variables.seanceId}/presences/${variables.presenceId}/signature`,
        { methode: 'POST', corps: { trace: variables.trace, consentement: true } },
      ),
    onSuccess: () => invalider(client),
  });
}

/**
 * Signature du formateur. En présentiel, elle se trace sur la tablette : l'API
 * refuse la signature sans tracé.
 */
export function useSignerFormateur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: { seanceId: string; trace: string }) =>
      appelApi<SeanceAffichee>(`/emargement/seances/${variables.seanceId}/signature-formateur`, {
        methode: 'POST',
        corps: { trace: variables.trace },
      }),
    onSuccess: () => invalider(client),
  });
}

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** PV archivé d'une séance, servi par l'API ; `version` pour une clôture antérieure. */
export function urlPvSeance(seanceId: string, version?: string): string {
  const requete = version ? `?${new URLSearchParams({ version }).toString()}` : '';
  return `${BASE}/emargement/seances/${seanceId}/pv${requete}`;
}

export interface SessionAuditable {
  sessionId: string;
  session: string;
  formationCode: string;
  formationTitre: string;
  siteCode: string;
  dateDebut: string;
  dateFin: string;
  premiereSeance: string;
  derniereSeance: string;
  seances: number;
}

/** Sessions qui ont des feuilles présentielles : celles qu'un export d'audit peut couvrir. */
export function useSessionsAudit(site: string, actif: boolean) {
  return useQuery({
    queryKey: ['emargement', 'audit', site],
    queryFn: () =>
      appelApi<{ sessions: SessionAuditable[] }>(
        `/emargement/audit/sessions?${new URLSearchParams({ site }).toString()}`,
      ),
    enabled: actif,
  });
}

/** Feuille d'émargement d'une session, une page par semaine, un mois au plus. */
export function urlFeuilleAudit(sessionId: string, du: string, au: string): string {
  return `${BASE}/emargement/audit/feuille.pdf?${new URLSearchParams({ sessionId, du, au }).toString()}`;
}

/**
 * Télécharge la feuille d'audit. Elle passe par `fetch` plutôt que par un lien :
 * une période sans feuille est refusée par l'API, et ce refus doit s'afficher
 * à l'écran, pas s'enregistrer comme un faux PDF.
 */
export async function telechargerFeuilleAudit(sessionId: string, du: string, au: string): Promise<void> {
  const reponse = await fetch(urlFeuilleAudit(sessionId, du, au), { credentials: 'include' });
  if (!reponse.ok) {
    const corps = (await reponse.json().catch(() => null)) as
      | { erreur?: { code?: string; message?: string } }
      | null;
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'ERREUR',
      corps?.erreur?.message ?? 'La feuille n’a pas pu être produite.',
    );
  }
  const nom =
    /filename="([^"]+)"/.exec(reponse.headers.get('content-disposition') ?? '')?.[1] ??
    'feuille-emargement.pdf';
  const url = URL.createObjectURL(await reponse.blob());
  const lien = document.createElement('a');
  lien.href = url;
  lien.download = nom;
  document.body.appendChild(lien);
  lien.click();
  document.body.removeChild(lien);
  URL.revokeObjectURL(url);
}

export interface SyntheseEmargementJour {
  jour: string;
  presentiel: {
    feuilles: number;
    aOuvrir: number;
    aCloturer: number;
    signes: number;
    attendus: number;
    absents: number;
  };
  distance: {
    classes: number;
    enDirect: number;
    camerasAllumees: number;
    camerasCoupees: number;
  };
}

/** Émargement du jour en chiffres, pour le hub pédagogique. */
export function useSyntheseEmargement(site: string, actif: boolean) {
  return useQuery({
    queryKey: ['emargement', 'synthese', site],
    queryFn: () =>
      appelApi<SyntheseEmargementJour>(
        `/emargement/synthese?${new URLSearchParams({ site }).toString()}`,
      ),
    enabled: actif,
  });
}

export function useCloturerSeance() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (seanceId: string) =>
      appelApi<{
        seance: SeanceAffichee;
        documentId: string | null;
        absencesConstatees: number;
        message: string;
      }>(`/emargement/seances/${seanceId}/cloture`, { methode: 'POST' }),
    onSuccess: () => invalider(client),
  });
}

export type ActionHistorique =
  | 'APPEL'
  | 'CORRECTION'
  | 'SIGNATURE'
  | 'CLOTURE'
  | 'REOUVERTURE'
  | 'JUSTIFICATION';

/** Instantané d'une ligne avant ou après une modification. */
export interface InstantanePresence {
  statut: StatutPresence;
  heureArrivee: string | null;
  heureDepart: string | null;
  justifiee: boolean;
  motifJustification: string | null;
  signeLe?: string;
}

export interface EvenementHistorique {
  id: string;
  action: ActionHistorique;
  actionLibelle: string;
  /** Vide pour un événement de feuille (clôture, réouverture). */
  presenceId: string | null;
  ancienneValeur: InstantanePresence | null;
  nouvelleValeur: InstantanePresence | null;
  motif: string | null;
  auteur: string | null;
  survenuLe: string;
}

export interface DetailPresence {
  seance: {
    id: string;
    libelle: string;
    formation: string | null;
    session: string | null;
    date: string;
    demiJournee: DemiJournee;
    demiJourneeLibelle: string;
    heureDebut: string;
    heureFin: string;
    statut: StatutSeance;
    clotureeLe: string | null;
    reouvertureEnAttente: DemandeEnAttente | null;
  };
  presence: PresenceAffichee;
  historique: EvenementHistorique[];
}

/** Détail d'une ligne : bilan du créneau et historique de ses modifications. */
export function useDetailPresence(seanceId: string | null, presenceId: string | null) {
  return useQuery({
    queryKey: ['emargement', 'detail', seanceId, presenceId],
    queryFn: () =>
      appelApi<DetailPresence>(`/emargement/seances/${seanceId}/presences/${presenceId}`),
    enabled: seanceId !== null && presenceId !== null,
  });
}

/**
 * Tracé d'une signature. Chargé à la demande seulement : chaque consultation
 * est tracée côté serveur, et rien n'est gardé en cache une fois la fenêtre
 * refermée.
 */
export function useSignaturePresence(
  seanceId: string | null,
  presenceId: string | null,
  actif: boolean,
) {
  return useQuery({
    queryKey: ['emargement', 'signature', seanceId, presenceId],
    queryFn: () =>
      appelApi<{ image: string; signeLe: string; empreinte: string | null }>(
        `/emargement/seances/${seanceId}/presences/${presenceId}/signature`,
      ),
    enabled: actif && seanceId !== null && presenceId !== null,
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Relit une feuille après un geste qui ne la rend pas : une demande de
 * modification sensible, ou une correction appliquée par la direction (TF-437).
 */
export function lireSeance(seanceId: string): Promise<SeanceAffichee> {
  return appelApi<SeanceAffichee>(`/emargement/seances/${seanceId}`);
}
